import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@/hooks/use-media-query'
import type { PreviewFile } from '@/hooks/use-upload-documents'

interface UploadImagesProps {
  onUpload: (files: FileList) => void
  files: PreviewFile[]
}

export const UploadImages: React.FC<UploadImagesProps> = ({
  onUpload,
  files
}) => {
  const { t } = useTranslation()

  const fileUpload = useRef<HTMLInputElement | null>(null)

  const isMobile = useMediaQuery('(max-width:599px)')

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files

    if (files) {
      onUpload(files)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    const files = event.dataTransfer.files

    if (files) onUpload(files)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  return (
    <div
      className="relative flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-gray-400 p-9"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {files.length === 0 ? (
        <div className="py-4">
          <svg
            className="size-14 text-gray-300"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      ) : null}
      <label className="cursor-pointer text-center text-sm font-medium text-gray-500 [text-wrap:pretty] before:absolute before:inset-0">
        <input
          ref={fileUpload}
          id="file-upload"
          name="file-upload"
          type="file"
          className="sr-only"
          multiple
          onChange={handleFileChange}
          accept="image/*"
        />
        {isMobile
          ? t('upload_images.take_or_upload_photo')
          : t('upload_images.drag_drop_photos')}
      </label>
    </div>
  )
}
